import React, { useEffect, useState } from 'react';
import Select, { components } from 'react-select';
import AsyncSelect from 'react-select/async';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm, Controller } from 'react-hook-form';
import CIcon from '@coreui/icons-react';
import {
  CForm,
  CFormGroup,
  CCol,
  CLabel,
  CInput,
  CFormText,
  CRow,
  CInputGroupPrepend,
  CInputGroupAppend,
  CInputGroup,
  CInputGroupText,
  CInputRadio,
  CInputCheckbox,
  CTextarea,
  CSwitch,
  CAlert,
  CLink,
} from '@coreui/react';
import get from 'lodash/get';
import { GraphQLError } from 'graphql';
import { FIELD_ERROR, FORM_ERROR, FormProps, IError, NotificationType, FormField, FormWrapper, FormRowWrapper } from '@transferz/models';
import { DayRangePicker } from '../dayRangePicker/DayRangePicker';
import { FileUpload } from '../fileUpload/FileUpload';

// from utils. Fix this part when we will decide how to deal with utils
export const getErrors = (graphQLError: GraphQLError): any => (
  graphQLError?.extensions?.response?.body?.errors
);

const formInput = (field: FormField, methods: any, defaultValue: any) => {
  const { register, errors, watch, getValues, setValue, trigger } = methods;
  return (
    <CInputGroup>
      {field.prepend && (
        <CInputGroupPrepend>
          <CInputGroupText>
            {field.prepend}
          </CInputGroupText>
        </CInputGroupPrepend>
      )}
      {field.prependIcon && (
        <CInputGroupPrepend>
          <CInputGroupText>
            <CIcon name={field.prependIcon} />
          </CInputGroupText>
        </CInputGroupPrepend>
      )}
      <CInput
        id={field.key}
        name={field.key}
        type={field.type}
        onChange={(event) => field.onChange && typeof field.onChange === 'function' && field.onChange(event, getValues, setValue)}
        onBlur={() => field.onBlur && typeof field.onBlur === 'function' && field.onBlur(trigger)}
        placeholder={field.placeholder || field.label as string}
        invalid={field.invalid || !!errors?.[field.key]}
        defaultValue={defaultValue === undefined ? field.defaultValue : defaultValue}
        innerRef={register(typeof field.validations === 'function' ? field.validations(getValues, watch) : field.validations) as any}
        disabled={typeof field.disabled === 'function' ? field.disabled(watch) : field.disabled}
        min={field?.min}
        max={field?.max}
        step={field?.step}
      />
      {field.append && (
        <CInputGroupAppend>
          <CInputGroupText>
            {field.append}
          </CInputGroupText>
        </CInputGroupAppend>
      )}
      {field.appendIcon && (
        <CInputGroupAppend>
          <CInputGroupText>
            <CIcon name={field.appendIcon} />
          </CInputGroupText>
        </CInputGroupAppend>
      )}
    </CInputGroup>
  );
};

const renderFields = (data: {[key: string]: any}, fields: (FormWrapper | FormField | FormRowWrapper)[], methods: any, filterOnlyDay?: boolean) => {
  const { register, errors, watch, getValues, control, setValue } = methods;
  return fields.map((field: FormWrapper | FormField | FormRowWrapper, idx: number) => {
    if (field.type === 'wrapper') {
      return (
        <CCol key={idx} className="no_padding">
          {renderFields(data, field._children, methods)}
        </CCol>
      );
    }

    if (field.type === 'row-wrapper') {
      return (
        <CRow key={idx} className="row-wrapper">
          {renderFields(data, field._children, methods)}
        </CRow>
      );
    }

    if (!field.key) {
      return null;
    }

    const defaultValue = get(data, field.key);
    return (
      <CCol xs={12} key={idx} className={field?.className}>
        <CFormGroup row={field.layout !== 'direction-col'} key={field.key}>
          {field.renderField && field.renderField(methods)}
          {field.type === 'header' && <strong className="ml-3">{field.label}</strong>}
          {field.layout === 'block' &&
            <CCol sm="12">
              {formInput(field, methods, defaultValue)}
              <CFormText color="danger">
                {errors?.[field.key]?.message}
              </CFormText>
            </CCol>
          }
          {!field.renderField && field.type !== 'header' && field.layout !== 'block' && (
            <>
              {typeof field.label === 'function'
                ? field.label(field)
                : <CLabel col htmlFor={field.key}>{field.label}</CLabel>}
              <CCol sm={field?.displaySize || '8'}>
                {field.renderInput && field.renderInput(methods)}
                {!field.renderInput && (
                  field.type === 'text' ||
                  field.type === 'number' ||
                  field.type === 'tel' ||
                  field.type === 'email' ||
                  field.type === 'password' ||
                  field.type === 'dynamicSearch'
                ) && formInput(field, methods, defaultValue)}
                {!field.renderInput && field.type === 'textarea' && (
                  <CTextarea
                    id={field.key}
                    name={field.key}
                    rows={field.rows}
                    placeholder={field.placeholder || field.label as string}
                    invalid={!!errors?.[field.key]}
                    defaultValue={defaultValue === undefined ? field.defaultValue : defaultValue}
                    innerRef={register(typeof field.validations === 'function' ? field.validations(getValues, watch) : field.validations) as any}
                    disabled={typeof field.disabled === 'function' ? field.disabled(watch) : field.disabled}
                  />
                )}
                {!field.renderInput && field.type === 'checkbox' &&
                  <>
                    {
                      (field.values as [])?.map((item: any, idx: number) => {
                        const { label, value, defaultChecked } = item;
                        let checked = false;
                        if (typeof data[field.key] === 'boolean') {
                          checked = data[field.key];
                        } else if (Array.isArray(data[field.key])) {
                          checked = data[field.key]?.includes(value);
                        }
                        return (
                          <CFormGroup variant="custom-checkbox" inline key={idx}>
                            <CInputCheckbox
                              key={idx}
                              onChange={(event) => field.onChange && typeof field.onChange === 'function' && field.onChange(event, getValues, setValue)}
                              className="mr-1"
                              name={field.key}
                              defaultChecked={checked || defaultChecked}
                              defaultValue={value}
                              disabled={typeof field.disabled === 'function' ? field.disabled(watch) : field.disabled}
                              innerRef={register(typeof field.validations === 'function' ? field.validations(getValues) : field.validations) as any}
                            />
                            {label && <CLabel variant="custom-checkbox" htmlFor={value}>{label}</CLabel>}
                          </CFormGroup>
                        )
                      })
                    }
                  </>
                }
                {!field.renderInput && field.type === 'color' &&
                  <input
                    name={field.key}
                    type="color"
                    defaultValue={field.defaultValue}
                    ref={register(typeof field.validations === 'function' ? field.validations(getValues) : field.validations) as any}
                  />
                }
                {!field.renderInput && field.type === 'file' &&
                  <FileUpload
                    name={field.key}
                    fileSrc={field?.fileUploadOptions?.fileSrc}
                    acceptType={field?.fileUploadOptions?.acceptType}
                    contentType={field?.fileUploadOptions?.contentType}
                    maxSize={field?.fileUploadOptions?.maxSize}
                    innerRef={register(typeof field.validations === 'function' ? field.validations(getValues) : field.validations) as any}
                    onFieldChange={(event: any) => field.onChange && typeof field.onChange === 'function' && field.onChange(event, getValues, setValue)}
                  />
                }
                {!field.renderInput && field.type === 'radio' &&
                  <>
                    {
                      (field.values as [])?.map((item: any, idx: number) => {
                        const { label, value } = item;
                        return (
                          <CFormGroup variant="custom-radio" inline key={idx}>
                            <CInputRadio
                              onChange={(e) => typeof field.getValue === 'function' && field.getValue((e.target as HTMLTextAreaElement).value)}
                              className="form-check-input"
                              id={value}
                              name={field.key}
                              innerRef={register(typeof field.validations === 'function' ? field.validations(getValues) : field.validations) as any}
                              defaultValue={value}
                              disabled={field.disabled as boolean}
                              defaultChecked={value === field.defaultValue}
                            />
                            <CLabel variant="custom-checkbox" htmlFor={value}>{label}</CLabel>
                          </CFormGroup>
                        )
                      })
                    }
                  </>
                }
                {!field.renderInput && field.type === 'switch' && (
                  <CSwitch
                    name={field.key}
                    color="primary"
                    defaultChecked={field.defaultChecked}
                    innerRef={register()}
                    size="lg"
                    disabled={field.disabled as boolean}
                    shape="pill"
                    onChange={field.onChange}
                  />
                )}
                {!field.renderInput && field.type === 'select' &&
                  <Controller
                    name={field.key}
                    render={({ onChange: onSelectChange, ref }: any) => (
                      <Select
                        onChange={(selected: any) => {
                          field.onChange && typeof field.onChange === 'function' && field.onChange(selected, setValue);
                          onSelectChange(selected, setValue);
                        }}
                        onInputChange={(newValue: string) => {
                          field.onInputChange && typeof field.onInputChange === 'function' && field.onInputChange(newValue, setValue);
                        }}
                        options={field.values as any}
                        defaultValue={field.defaultValue}
                        isDisabled={typeof field.disabled === 'function' ? field.disabled(watch) : field.disabled}
                        components={{
                          GroupHeading: (props: any) => (
                            field.selectOptions?.isGrouped
                              ? <components.GroupHeading {...props} />
                              : null
                          ),
                        }}
                        styles={field.selectOptions?.isGrouped
                          ? {
                            groupHeading: (base: any) => ({
                              ...base,
                              color: 'black',
                              fontSize: 14
                            })
                          }
                          : undefined
                        }
                        isMulti={field?.isMulti}
                        placeholder={field?.placeholder}
                      />
                    )}
                    rules={typeof field.validations === 'function' ? field.validations(getValues) : field.validations}
                    control={control}
                    defaultValue={field.defaultValue}
                  />
                }
                {!field.renderInput && field.type === 'autocomplete' &&
                  <Controller
                    name={field.key}
                    render={({ onChange: onSelectChange, ref }: any) => (
                      <AsyncSelect
                        key={field.fieldKey || field.key}
                        onChange={(selected: any) => {
                          field.onChange && typeof field.onChange === 'function' && field.onChange(selected, field);
                          onSelectChange(selected);
                        }}
                        loadOptions={(inputValue: string, callback: Function) => {
                          field.values && typeof field.values === 'function' && field.values(inputValue, field, callback);
                        }}
                        isMulti={field?.isMulti}
                        isDisabled={typeof field.disabled === 'function' ? field.disabled(watch) : field.disabled}
                        value={field.defaultValue}
                        defaultOptions={field?.defaultOptions}
                        noOptionsMessage={() => field.placeholder || null}
                        cacheOptions
                      />
                    )}
                    rules={typeof field.validations === 'function' ? field.validations(getValues) : field.validations}
                    control={control}
                    defaultValue={field.defaultValue}
                  />
                }
                {!field.renderInput && field.type === 'autosuggest' &&
                  <Controller
                    name={field.key}
                    render={({ onChange: onSelectChange, ref }: any) => (
                      <AsyncSelect
                        key={field.fieldKey || field.key}
                        onChange={(selected: any) => {
                          field.onChange && typeof field.onChange === 'function' && field.onChange(selected);
                          onSelectChange(selected);
                        }}
                        loadOptions={field.values as any}
                        isMulti={field?.isMulti}
                        isDisabled={typeof field.disabled === 'function' ? field.disabled(watch) : field.disabled}
                        defaultValue={field.defaultValue}
                        defaultOptions={field?.defaultOptions}
                        noOptionsMessage={() => "Type to select"}
                        cacheOptions
                      />
                    )}
                    rules={typeof field.validations === 'function' ? field.validations(getValues) : field.validations}
                    control={control}
                    defaultValue={field.defaultValue}
                  />
                }
                {!field.renderInput && field.type === 'info' &&
                  <span className="form-info-field">{field.defaultValue || defaultValue || field.emptyText || 'N/A'}</span>
                }
                {!field.renderInput && field.type === 'link' && (
                  field?.defaultValue || defaultValue
                    ? <CLink className="form-info-field" href={field.link}>{field?.defaultValue || defaultValue}</CLink>
                    : <span className="form-info-field">{field.emptyText || 'N/A'}</span>
                )}
                {!field.renderInput && field.type === 'date-time' &&
                  <>
                    <div className="date-time-wrapper">
                      <div className="date-picker">
                        <CInput
                          type="date"
                          name={field.dateInputOptions?.fromDateInputName}
                          placeholder={field.dateInputOptions?.dateInputPlaceholder}
                          innerRef={register(typeof field.validations === 'function' && field.validations(methods))}
                          defaultValue={field.dateInputOptions?.fromDateInputDefaultValue}
                          onChange={(date) => typeof field.dateInputEvents?.onDateChange === 'function' && field.dateInputEvents?.onDateChange(date, getValues, setValue)}
                          min={field.min}
                        />
                      </div>
                      <CInput
                        name={field.dateInputOptions?.fromHourInputName}
                        type="tel"
                        className="mr-1"
                        placeholder={field.dateInputOptions?.hourInputPlaceholder}
                        innerRef={register(typeof field.validations === 'function' && field.validations(methods))}
                        defaultValue={field.dateInputOptions?.fromHourInputDefaultValue}
                        onChange={(hour) => typeof field.dateInputEvents?.onHourChange === 'function' && field.dateInputEvents?.onHourChange(hour, getValues, setValue)}
                      />
                      <strong>:</strong>
                      <CInput
                        name={field.dateInputOptions?.fromMinuteInputName}
                        type="tel"
                        className="mr-1"
                        placeholder={field.dateInputOptions?.minuteInputPlaceholder}
                        innerRef={register(typeof field.validations === 'function' && field.validations(methods))}
                        defaultValue={field.dateInputOptions?.fromMinuteInputDefaultValue}
                        onChange={(minute) => typeof field.dateInputEvents?.onMinuteChange === 'function' && field.dateInputEvents?.onMinuteChange(minute,  getValues, setValue)}
                      />
                    </div>
                    {field.dateTimeText && <div className="mt-1">{field.dateTimeText}</div>}
                  </>
                }
                {!field.renderInput && field.type === 'time-range' &&
                  <div className="time-range-wrapper">
                    <CInput
                      name={field.dateInputOptions?.fromHourInputName}
                      type="tel"
                      innerRef={register(typeof field.validations === 'function' && field.validations(methods))}
                      defaultValue={field.dateInputOptions?.fromHourInputDefaultValue}
                    />
                    <strong className="mr-1 ml-1">:</strong>
                    <CInput
                      name={field.dateInputOptions?.fromMinuteInputName}
                      type="tel"
                      className="mr-1"
                      innerRef={register(typeof field.validations === 'function' && field.validations(methods))}
                      defaultValue={field.dateInputOptions?.fromMinuteInputDefaultValue}
                    />
                    <span className="mr-2 ml-2">to</span>
                    <CInput
                      name={field.dateInputOptions?.toHourInputName}
                      type="tel"
                      innerRef={register(typeof field.validations === 'function' && field.validations(methods))}
                      defaultValue={field.dateInputOptions?.toHourInputDefaultValue}
                    />
                    <strong className="mr-1 ml-1">:</strong>
                    <CInput
                      name={field.dateInputOptions?.toMinuteInputName}
                      type="tel"
                      className="mr-1"
                      innerRef={register(typeof field.validations === 'function' && field.validations(methods))}
                      defaultValue={field.dateInputOptions?.toMinuteInputDefaultValue}
                    />
                  </div>
                }
                {!field.renderInput && field.type === 'date-range-picker' &&
                  <div className="date-range-wrapper">
                    <Controller
                      name={field.key}
                      render={() => (
                        <DayRangePicker
                          filterOnlyDay={filterOnlyDay}
                          name={field.key}
                          setValue={setValue}
                          orientation={field.orientation}
                          defaultValue={{
                            startDate: field.defaultValue?.startDate,
                            endDate: field.defaultValue?.endDate,
                          }}
                        />
                      )}
                      rules={typeof field.validations === 'function' ? field.validations(getValues) : field.validations}
                      control={control}
                    />
                  </div>
                }
                {!field.renderInput && field.type === 'date' &&
                  <div className="date-picker">
                    <CInput
                      type="date"
                      name={field.key}
                      placeholder="Date (YYYY-MM-DD)"
                      innerRef={register()}
                      defaultValue={field.defaultValue}
                    />
                  </div>
                }
                <CFormText color="danger">
                  {errors?.[field.key]?.message}
                </CFormText>
              </CCol>
            </>
          )}
        </CFormGroup>
      </CCol >
    );
  });
};

export const Form = ({
  data,
  fields,
  onSubmit,
  defaultValues = {},
  id = 'form',
  formErrorFieldName = 'formError',
  innerRef = null,
  filterOnlyDay = false,
}: FormProps) => {
  const { t, i18n } = useTranslation();
  const methods = useForm({ defaultValues, mode: 'onTouched' });
  const { setError } = methods;
  const [formError, setFormError] = useState();

  useEffect(() => {
    const lng = localStorage.getItem('preferredLanguage') || '';
    lng && i18n.changeLanguage(JSON.parse(lng));
  }, []);

  return (
    <FormProvider {...methods}>
      <CForm
        innerRef={innerRef}
        id={id}
        className="form-horizontal"
        onSubmit={methods.handleSubmit((data: any) => {
          (onSubmit as Function)(data)
            .catch((error: any) => {
              error?.graphQLErrors?.forEach((graphQLError: any) => {
                const errors = getErrors(graphQLError);
                errors?.forEach(({ code, field }: IError) => {
                  //TODO Ask backend guys to change the error code as we already had one for the email uniqueness, and remove this if after that
                  if (code === 'UNIQUE_CONSTRAINT' && field === 'name') {
                    setError('name', { message: 'Organization name must be unique' });
                  } else if (FIELD_ERROR[code]) {
                    const { message } = FIELD_ERROR[code];
                    let errorMsg: string = message;
                    if (i18n.exists(message)) {
                      errorMsg = t(message);
                    }
                    const fieldName = field || formErrorFieldName;
                    setError(fieldName, { message: errorMsg });
                  } else {
                    if (FORM_ERROR[code]) {
                      const { message } = FORM_ERROR[code];
                      setFormError(message);
                    }
                  }
                });
              });
            });
        })}
      >
        <CRow>
          {renderFields(data, fields, methods, filterOnlyDay)}
        </CRow>
      </CForm>
      {formError && (
        <CAlert color={NotificationType.Danger} className="mt-2 mb-2">
          {formError}
        </CAlert>
      )}
    </FormProvider>
  );
};
